/**
 * The seeded taxonomy's words, in every language the app speaks.
 *
 * The database stores a rating's *wording* — `name_snapshot`, `stops_snapshot` —
 * because a session's taxonomy is frozen at start and an old review must never be
 * rewritten by a later edit. That rule is not negotiable and this module does not
 * touch it. What it adds is a second, weaker claim alongside the snapshot: a
 * `seed_key` saying "this row came out of the shipped base set". When that key is
 * present the screens render the catalogue entry for the reader's language
 * instead of the stored string; when it is absent — a question somebody wrote
 * themselves, or a key this build has never heard of — the stored string is what
 * shows, unchanged.
 *
 * So the snapshot is the durable fallback and stays authoritative for anything
 * off the shelf, and switching language is a render-time decision that writes
 * nothing. A night rated in German and read in French shows French words over the
 * same numbers, and a build rolled back to before a key existed still renders the
 * English the row was seeded with rather than a blank.
 *
 * ## Why the entries are tuples
 *
 * Six strings per question — the name and five stop labels, lowest first — for
 * forty-two questions in three languages. Written as objects that is eight
 * hundred lines of punctuation around the content; written as tuples the file
 * reads as what it is, a translation table, and a missing label is a length error
 * the compiler catches rather than an `undefined` that reaches a track.
 *
 * The five labels are per question rather than shared, so "Endless waiting" can
 * be the bottom of downtime while the bottom of theme is "Nothing happens". A
 * shared ladder would have made every question's floor the same sentence, which
 * is the thing that makes a long rubric feel like a form.
 *
 * ## French is unreviewed
 *
 * `UNREVIEWED` names the locales no native speaker has read. It is not decoration
 * — the settings screen says so next to the option, because a wrong word in a
 * rating scale changes what people answer, and that is worse than a missing
 * translation.
 */

import type { Stop } from "./types.ts";

export type Locale = "de" | "en" | "fr";

export const LOCALES = ["de", "en", "fr"] as const;

/**
 * English, not German. The seed rows are written into the database in this
 * language, so it is the one that has to be readable to anybody whose build does
 * not know a key — which includes every export opened somewhere else.
 */
export const DEFAULT_LOCALE: Locale = "en";

/** Locales written by this project rather than by somebody who speaks them. */
export const UNREVIEWED: readonly Locale[] = ["fr"];

/** Each language's name in itself, which is the only form a picker should use. */
export const LOCALE_NAMES: Record<Locale, string> = {
  de: "Deutsch",
  en: "English",
  fr: "Français",
};

export function isLocale(value: unknown): value is Locale {
  return typeof value === "string" && (LOCALES as readonly string[]).includes(value);
}

/**
 * The closest locale this app has to a browser's preference.
 *
 * Matched on the primary subtag alone: `de-AT`, `de-CH` and `de` all want German,
 * and a table in Vienna being handed English because of a region code would be a
 * bug nobody would think to report.
 */
export function matchLocale(tags: readonly string[]): Locale {
  for (const tag of tags) {
    const primary = tag.toLowerCase().split("-")[0];
    if (isLocale(primary)) return primary;
  }
  return DEFAULT_LOCALE;
}

// ---------------------------------------------------------------------------
// The shipped base set
// ---------------------------------------------------------------------------

export interface SeedTopic {
  key: string;
  /** Follow-up keys, in the order they are asked. */
  subquestions: readonly string[];
}

/**
 * Six topics, six follow-ups each, and the order is the order of the night.
 *
 * Keys are stable identifiers and are never shown. Renaming one is a migration,
 * not an edit: an existing account's rows are matched to this list by key, so a
 * changed key seeds a duplicate topic and orphans the old one.
 */
export const SEED_TOPICS: readonly SeedTopic[] = [
  {
    key: "components",
    subquestions: [
      "components.quality",
      "components.presence",
      "components.legibility",
      "components.footprint",
      "components.insert",
      "components.handling",
    ],
  },
  {
    key: "rules",
    subquestions: [
      "rules.structure",
      "rules.examples",
      "rules.lookup",
      "rules.teaching",
      "rules.aids",
      "rules.payoff",
    ],
  },
  {
    key: "mechanics",
    subquestions: [
      "mechanics.core",
      "mechanics.downtime",
      "mechanics.upkeep",
      "mechanics.luck",
      "mechanics.cohesion",
      "mechanics.runaway",
    ],
  },
  {
    key: "depth",
    subquestions: [
      "depth.paths",
      "depth.meaningful",
      "depth.flexibility",
      "depth.replay",
      "depth.longterm",
      "depth.playercount",
    ],
  },
  {
    key: "theme",
    subquestions: [
      "theme.integration",
      "theme.moments",
      "theme.interaction",
      "theme.talk",
      "theme.quarterbacking",
      "theme.stories",
    ],
  },
  {
    key: "pacing",
    subquestions: [
      "pacing.length",
      "pacing.tension",
      "pacing.lulls",
      "pacing.climax",
      "pacing.trigger",
      "pacing.again",
    ],
  },
];

/** Every key the base set defines, topics and follow-ups together. */
export const SEED_KEYS: readonly string[] = SEED_TOPICS.flatMap((t) => [t.key, ...t.subquestions]);

/** How many stops every seeded question has. Five labels, five stars. */
export const SEED_STOP_COUNT = 5;

/** `[name, ...five stop labels]`, worst first. */
export type Entry = readonly [string, string, string, string, string, string];

// ---------------------------------------------------------------------------
// Deutsch
// ---------------------------------------------------------------------------

const DE: Record<string, Entry> = {
  components: [
    "Material & Tischpräsenz",
    "Billig",
    "Zweckmäßig",
    "Solide",
    "Hochwertig",
    "Prachtstück",
  ],
  "components.quality": [
    "Wie hochwertig, robust und langlebig fühlen sich Karten, Holz- und Plastikteile an?",
    "Billig",
    "Zweckmäßig",
    "Solide",
    "Wertig",
    "Erstklassig",
  ],
  "components.presence": [
    "Bietet das Spiel durch Grafik und Miniaturen/Token eine beeindruckende Tischpräsenz?",
    "Unscheinbar",
    "Zurückhaltend",
    "Ansehnlich",
    "Ein Blickfang",
    "Ein Spektakel",
  ],
  "components.legibility": [
    "Ist das Design (Symbole und Texte) auch von der anderen Tischseite aus gut lesbar?",
    "Nur aus der Nähe",
    "Ständiges Nachfragen",
    "Meist lesbar",
    "Gut lesbar",
    "Von überall klar",
  ],
  "components.footprint": [
    "Passen alle Komponenten für einen flüssigen Spielablauf gut auf einen Standardtisch?",
    "Sprengt den Tisch",
    "Wird eng",
    "Passt knapp",
    "Passt gut",
    "Viel Luft",
  ],
  "components.insert": [
    "Unterstützt das Inlay der Box das schnelle Sortieren, Auf- und Abbauen des Spiels?",
    "Kein Inlay",
    "Stört eher",
    "Geht so",
    "Hilft spürbar",
    "Perfekt sortiert",
  ],
  "components.handling": [
    "Ermöglicht die physische Gestaltung ein frustfreies Greifen und Verschieben der Teile?",
    "Frickelig",
    "Umständlich",
    "Geht gut",
    "Angenehm",
    "Griffig und leicht",
  ],

  rules: ["Regelwerk & Einstieg", "Zäher Einstieg", "Mühsam", "Machbar", "Klarer Einstieg", "Vorbildlich"],
  "rules.structure": [
    "Wie logisch, übersichtlich und fehlerfrei ist das physische Regelheft strukturiert?",
    "Chaotisch",
    "Lückenhaft",
    "Brauchbar",
    "Klar aufgebaut",
    "Fehlerfrei",
  ],
  "rules.examples": [
    "Helfen bebilderte Beispiele und Aufbau-Diagramme beim schnellen Verständnis?",
    "Keine Beispiele",
    "Kaum hilfreich",
    "Ganz nützlich",
    "Sehr hilfreich",
    "Erklärt alles",
  ],
  "rules.lookup": [
    "Sind Regelfragen oder Sonderfälle während des Spiels schnell im Heft auffindbar?",
    "Nie gefunden",
    "Langes Suchen",
    "Mit viel Blättern",
    "Schnell gefunden",
    "Sofort da",
  ],
  "rules.teaching": [
    "Wie intuitiv und eingängig lässt sich der Rundenablauf neuen Spielern erklären?",
    "Kaum erklärbar",
    "Mühsam",
    "Solide",
    "Eingängig",
    "Erklärt sich fast selbst",
  ],
  "rules.aids": [
    "Bietet das Spiel nützliche und vollständige Übersichtskarten (Player Aids) für alle?",
    "Keine Übersicht",
    "Unvollständig",
    "Brauchbar",
    "Gut gemacht",
    "Alles drauf",
  ],
  "rules.payoff": [
    "Ist der Lernaufwand für die spielerische Tiefe, die man zurückbekommt, angemessen?",
    "Aufwand umsonst",
    "Zu viel Lernen",
    "Fair",
    "Lohnt sich",
    "Jede Minute wert",
  ],

  mechanics: ["Spielmechanik & Fluss", "Hakt überall", "Zäh", "Läuft", "Rund", "Wie geölt"],
  "mechanics.core": [
    "Wie innovativ, elegant und befriedigend fühlt sich der mechanische Kern an?",
    "Abgestanden",
    "Schon bekannt",
    "Solide",
    "Elegant",
    "Frisch und befriedigend",
  ],
  "mechanics.downtime": [
    "Bleibt die Downtime (Wartezeit) zwischen den eigenen Zügen angenehm kurz?",
    "Endloses Warten",
    "Lange Pausen",
    "Erträglich",
    "Kurz",
    "Kaum Wartezeit",
  ],
  "mechanics.upkeep": [
    "Funktionieren die Verwaltungsphasen (Upkeep) zwischen Runden ohne zähen Aufwand?",
    "Reine Buchhaltung",
    "Zäh",
    "Geht so",
    "Flott",
    "Fast unsichtbar",
  ],
  "mechanics.luck": [
    "Ist die Balance zwischen taktischem Würfel-/Kartenglück und Strategie ausgewogen?",
    "Glück entscheidet",
    "Zu zufällig",
    "Ausgewogen",
    "Gut austariert",
    "Glück nur als Würze",
  ],
  "mechanics.cohesion": [
    "Greifen die verschiedenen Phasen und Mechanismen nahtlos und logisch ineinander?",
    "Zusammengestückelt",
    "Holprig",
    "Passt",
    "Greift ineinander",
    "Wie aus einem Guss",
  ],
  "mechanics.runaway": [
    "Verhindert das System wirksam, dass ein Spieler uneinholbar davonzieht (Runaway Leader)?",
    "Früh entschieden",
    "Kaum aufholbar",
    "Meist offen",
    "Bleibt spannend",
    "Bis zum Schluss offen",
  ],

  depth: ["Strategie & Varianz", "Flach", "Dünn", "Ordentlich", "Tief", "Unerschöpflich"],
  "depth.paths": [
    "Haben Spieler echte Entscheidungsfreiheit über ihren eigenen Weg zum Sieg?",
    "Ein Weg",
    "Kaum Wahl",
    "Zwei, drei Wege",
    "Viele Wege",
    "Ganz eigener Weg",
  ],
  "depth.meaningful": [
    "Sind die Entscheidungen in jedem Zug bedeutungsvoll statt offensichtlich vorgegeben?",
    "Zug ist vorgegeben",
    "Meist offensichtlich",
    "Manchmal knifflig",
    "Fast immer schwer",
    "Jeder Zug schmerzt",
  ],
  "depth.flexibility": [
    "Ermöglicht das Spiel flexible Taktikwechsel, wenn Pläne durchkreuzt werden?",
    "Plan oder nichts",
    "Kaum Umsteuern",
    "Etwas Spielraum",
    "Gut anpassbar",
    "Immer ein Ausweg",
  ],
  "depth.replay": [
    "Bietet das Spiel durch variablen Aufbau oder Kartenpakete einen hohen Wiederspielwert?",
    "Einmal reicht",
    "Wenig Abwechslung",
    "Ein paar Partien",
    "Lange frisch",
    "Nie zweimal gleich",
  ],
  "depth.longterm": [
    "Wie tief und belohnend ist das langfristige Planen gegenüber kurzfristigem Profit?",
    "Nur Reagieren",
    "Kurzfristig",
    "Etwas Planung",
    "Belohnt Planung",
    "Große Bögen zahlen sich aus",
  ],
  "depth.playercount": [
    "Reagiert das System dynamisch und fair auf unterschiedliche Spieleranzahlen?",
    "Nur in einer Besetzung",
    "Wackelig",
    "Meist stimmig",
    "Skaliert gut",
    "In jeder Besetzung stark",
  ],

  theme: ["Atmosphäre & Interaktion", "Kalt", "Blass", "Stimmig", "Lebendig", "Elektrisierend"],
  "theme.integration": [
    "Spürt man das Thema direkt in den Mechaniken, statt nur auf den Bildern?",
    "Nur Deko",
    "Aufgeklebt",
    "Erkennbar",
    "Trägt die Mechanik",
    "Untrennbar",
  ],
  "theme.moments": [
    "Erzeugt das Spiel emotionale Momente (Spannung, Erleichterung, Überraschung) am Tisch?",
    "Nichts passiert",
    "Selten",
    "Ab und zu",
    "Regelmäßig",
    "Ständig Gänsehaut",
  ],
  "theme.interaction": [
    "Wie stark und bedeutungsvoll ist die Interaktion (direkt oder indirekt) zwischen den Spielern?",
    "Solitär",
    "Kaum Berührung",
    "Indirekt",
    "Deutlich spürbar",
    "Ständig verzahnt",
  ],
  "theme.talk": [
    "Fördert das Spiel lebendige Gespräche, Verhandlungen oder gesunde Rivalität?",
    "Stille am Tisch",
    "Wenig Gespräch",
    "Etwas Austausch",
    "Lebhaft",
    "Der Tisch tobt",
  ],
  "theme.quarterbacking": [
    "Werden typische Probleme wie „Quarterbacking“ verhindert?",
    "Einer sagt an",
    "Oft dominiert",
    "Kommt vor",
    "Selten",
    "Nie ein Thema",
  ],
  "theme.stories": [
    "Entstehen durch das Zusammenspiel der Spieler denkwürdige, eigene Geschichten?",
    "Nichts bleibt",
    "Schnell vergessen",
    "Eine Szene",
    "Mehrere Momente",
    "Wird noch erzählt",
  ],

  pacing: ["Pacing & Spannungsbogen", "Zieht sich", "Ungleich", "Passt", "Gut getaktet", "Perfekter Bogen"],
  "pacing.length": [
    "Passt die tatsächliche Spieldauer zur Komplexität und zum Spielspaß des Erlebnisses?",
    "Viel zu lang",
    "Etwas lang",
    "Passt",
    "Gut bemessen",
    "Keine Minute zu viel",
  ],
  "pacing.tension": [
    "Steigt die Spannung im Laufe der Partie spürbar und kontinuierlich an?",
    "Flacht ab",
    "Bleibt gleich",
    "Steigt etwas",
    "Zieht spürbar an",
    "Steigert sich stetig",
  ],
  "pacing.lulls": [
    "Ist das Spiel frei von zähen Phasen, in denen sich das Geschehen im Kreis dreht?",
    "Ständig zäh",
    "Mehrere Hänger",
    "Ein Durchhänger",
    "Kaum Leerlauf",
    "Durchweg flott",
  ],
  "pacing.climax": [
    "Baut sich zum Spielende hin ein echter, dramatischer Höhepunkt (Climax) auf?",
    "Verpufft",
    "Endet einfach",
    "Solider Schluss",
    "Starkes Finale",
    "Großer Höhepunkt",
  ],
  "pacing.trigger": [
    "Ist das Spielende-Ereignis (Trigger) klar, elegant und für alle fair absehbar?",
    "Kommt aus dem Nichts",
    "Unklar",
    "Erkennbar",
    "Klar absehbar",
    "Elegant und fair",
  ],
  "pacing.again": [
    "Hinterlässt das Finale bei allen Spielern das sofortige Bedürfnis, noch eine Partie zu spielen?",
    "Nie wieder",
    "Erstmal genug",
    "Irgendwann gern",
    "Bald wieder",
    "Sofort nochmal",
  ],
};

// ---------------------------------------------------------------------------
// English
// ---------------------------------------------------------------------------

const EN: Record<string, Entry> = {
  components: [
    "Components & Table Presence",
    "Cheap",
    "Functional",
    "Solid",
    "Premium",
    "A showpiece",
  ],
  "components.quality": [
    "How solid, sturdy and long-lasting do the cards, wood and plastic feel?",
    "Feels cheap",
    "Functional",
    "Solid",
    "Premium",
    "Built to last",
  ],
  "components.presence": [
    "Do the artwork and the miniatures or tokens give the game real table presence?",
    "Invisible",
    "Understated",
    "Handsome",
    "An eye-catcher",
    "A spectacle",
  ],
  "components.legibility": [
    "Are the symbols and text readable from the other side of the table?",
    "Only up close",
    "Constant asking",
    "Mostly readable",
    "Easy to read",
    "Clear from anywhere",
  ],
  "components.footprint": [
    "Does everything fit on a normal table without cramping play?",
    "Overruns the table",
    "Gets cramped",
    "Just fits",
    "Fits well",
    "Room to spare",
  ],
  "components.insert": [
    "Does the box insert help with sorting, setup and packing away?",
    "No insert",
    "Gets in the way",
    "Passable",
    "Genuinely helps",
    "Perfectly sorted",
  ],
  "components.handling": [
    "Are the pieces frustration-free to pick up and move around?",
    "Fiddly",
    "Awkward",
    "Fine",
    "Comfortable",
    "Effortless",
  ],

  rules: ["Rules & Onboarding", "A slog", "Hard work", "Manageable", "A clear start", "Exemplary"],
  "rules.structure": [
    "How logical, clear and error-free is the printed rulebook?",
    "Chaotic",
    "Full of gaps",
    "Usable",
    "Well ordered",
    "Flawless",
  ],
  "rules.examples": [
    "Do illustrated examples and setup diagrams speed up understanding?",
    "None at all",
    "Barely help",
    "Somewhat useful",
    "Very helpful",
    "Explain everything",
  ],
  "rules.lookup": [
    "Can a rules question or edge case be found quickly mid-game?",
    "Never found",
    "A long hunt",
    "With some flipping",
    "Found fast",
    "Instantly there",
  ],
  "rules.teaching": [
    "How intuitively can the turn sequence be taught to new players?",
    "Barely teachable",
    "Hard work",
    "Solid",
    "Intuitive",
    "Almost self-explaining",
  ],
  "rules.aids": [
    "Are there useful, complete player aids for everyone at the table?",
    "None",
    "Incomplete",
    "Usable",
    "Well made",
    "Everything on them",
  ],
  "rules.payoff": [
    "Is the learning effort worth the depth you get back?",
    "Effort wasted",
    "Too much to learn",
    "Fair",
    "Worth it",
    "Worth every minute",
  ],

  mechanics: ["Mechanics & Flow", "Snags everywhere", "Sluggish", "It runs", "Smooth", "Frictionless"],
  "mechanics.core": [
    "How innovative, elegant and satisfying does the mechanical core feel?",
    "Stale",
    "Familiar",
    "Solid",
    "Elegant",
    "Fresh and satisfying",
  ],
  "mechanics.downtime": [
    "Does the downtime between your own turns stay comfortably short?",
    "Endless waiting",
    "Long pauses",
    "Bearable",
    "Short",
    "Barely any wait",
  ],
  "mechanics.upkeep": [
    "Do the upkeep phases between rounds run without dragging?",
    "Pure bookkeeping",
    "A drag",
    "Passable",
    "Brisk",
    "Almost invisible",
  ],
  "mechanics.luck": [
    "Is the balance between tactical dice or card luck and strategy right?",
    "Luck decides",
    "Too random",
    "Balanced",
    "Well judged",
    "Luck as seasoning",
  ],
  "mechanics.cohesion": [
    "Do the phases and mechanisms mesh seamlessly and logically?",
    "Bolted together",
    "Bumpy",
    "It fits",
    "Meshes well",
    "All of a piece",
  ],
  "mechanics.runaway": [
    "Does the system stop one player from running away with it for good?",
    "Decided early",
    "No catching up",
    "Usually open",
    "Stays tense",
    "Open to the last",
  ],

  depth: ["Depth & Replayability", "Flat", "Thin", "Decent", "Deep", "Bottomless"],
  "depth.paths": [
    "Do players have real freedom over their own route to victory?",
    "One route",
    "Barely a choice",
    "Two or three routes",
    "Many routes",
    "A route of your own",
  ],
  "depth.meaningful": [
    "Are the decisions each turn meaningful rather than obviously prescribed?",
    "The turn plays itself",
    "Mostly obvious",
    "Sometimes tricky",
    "Nearly always hard",
    "Every turn hurts",
  ],
  "depth.flexibility": [
    "Can you switch tactics when your plans are cut off?",
    "The plan or nothing",
    "Barely steerable",
    "Some room",
    "Adapts well",
    "Always a way out",
  ],
  "depth.replay": [
    "Do variable setup or card packs give it real replay value?",
    "Once is enough",
    "Little variety",
    "A few plays",
    "Stays fresh",
    "Never the same twice",
  ],
  "depth.longterm": [
    "How deep and rewarding is long-term planning against short-term gain?",
    "Only reacting",
    "Short-term",
    "Some planning",
    "Rewards planning",
    "Long arcs pay off",
  ],
  "depth.playercount": [
    "Does the system respond dynamically and fairly to different player counts?",
    "One count only",
    "Shaky",
    "Mostly right",
    "Scales well",
    "Strong at every count",
  ],

  theme: ["Theme & Table Dynamics", "Cold", "Pale", "Fitting", "Alive", "Electric"],
  "theme.integration": [
    "Is the theme felt in the mechanics rather than only in the art?",
    "Pure decoration",
    "Pasted on",
    "Recognisable",
    "Carries the mechanics",
    "Inseparable",
  ],
  "theme.moments": [
    "Does the game create emotional moments — tension, relief, surprise — at the table?",
    "Nothing happens",
    "Rarely",
    "Now and then",
    "Regularly",
    "Constant goosebumps",
  ],
  "theme.interaction": [
    "How strong and meaningful is the interaction, direct or indirect, between players?",
    "Solitaire",
    "Barely touching",
    "Indirect",
    "Clearly felt",
    "Constantly entangled",
  ],
  "theme.talk": [
    "Does the game spark lively talk, negotiation or healthy rivalry?",
    "Silence at the table",
    "Little talk",
    "Some back and forth",
    "Lively",
    "The table roars",
  ],
  "theme.quarterbacking": [
    "Are problems like quarterbacking kept out?",
    "One person calls it",
    "Often dominated",
    "It happens",
    "Rarely",
    "Never an issue",
  ],
  "theme.stories": [
    "Does the play between people produce memorable stories of your own?",
    "Nothing sticks",
    "Soon forgotten",
    "One scene",
    "Several moments",
    "Still being retold",
  ],

  pacing: ["Pacing & Climax", "Drags", "Uneven", "Fine", "Well timed", "A perfect arc"],
  "pacing.length": [
    "Does the actual playing time match the complexity and the fun of it?",
    "Far too long",
    "A bit long",
    "About right",
    "Well judged",
    "Not a minute wasted",
  ],
  "pacing.tension": [
    "Does the tension build noticeably and steadily as the game goes on?",
    "Flattens out",
    "Stays level",
    "Rises a little",
    "Clearly builds",
    "Builds all the way",
  ],
  "pacing.lulls": [
    "Is the game free of slow phases where things go round in circles?",
    "Constant drag",
    "Several lulls",
    "One lull",
    "Barely any",
    "Brisk throughout",
  ],
  "pacing.climax": [
    "Does a real, dramatic climax build towards the end?",
    "Fizzles out",
    "Just stops",
    "Solid ending",
    "Strong finale",
    "A real climax",
  ],
  "pacing.trigger": [
    "Is the end trigger clear, elegant and fairly visible to everyone?",
    "Out of nowhere",
    "Unclear",
    "Visible",
    "Clearly coming",
    "Elegant and fair",
  ],
  "pacing.again": [
    "Does the finish leave everyone wanting another round straight away?",
    "Never again",
    "Enough for now",
    "Some day",
    "Soon again",
    "Straight away",
  ],
};

// ---------------------------------------------------------------------------
// Français — unreviewed, see UNREVIEWED above
// ---------------------------------------------------------------------------

const FR: Record<string, Entry> = {
  components: [
    "Composants & Présence sur table",
    "Bas de gamme",
    "Fonctionnel",
    "Solide",
    "Haut de gamme",
    "Pièce d’exposition",
  ],
  "components.quality": [
    "Les cartes, le bois et le plastique semblent-ils solides et durables ?",
    "Bon marché",
    "Fonctionnel",
    "Solide",
    "De qualité",
    "Fait pour durer",
  ],
  "components.presence": [
    "Les illustrations et les figurines ou jetons donnent-ils une vraie présence sur table ?",
    "Invisible",
    "Discret",
    "Beau",
    "Accrocheur",
    "Spectaculaire",
  ],
  "components.legibility": [
    "Les symboles et les textes se lisent-ils depuis l’autre bout de la table ?",
    "Seulement de près",
    "On demande sans cesse",
    "Lisible en général",
    "Facile à lire",
    "Clair de partout",
  ],
  "components.footprint": [
    "Tout tient-il sur une table normale sans gêner le déroulement ?",
    "Déborde de la table",
    "Ça devient serré",
    "Ça tient juste",
    "Tient bien",
    "De la place de reste",
  ],
  "components.insert": [
    "Le rangement de la boîte aide-t-il au tri, à la mise en place et au rangement ?",
    "Aucun rangement",
    "Plutôt gênant",
    "Passable",
    "Aide vraiment",
    "Parfaitement trié",
  ],
  "components.handling": [
    "La forme des pièces permet-elle de les saisir et de les déplacer sans frustration ?",
    "Pénible",
    "Peu pratique",
    "Correct",
    "Agréable",
    "Sans effort",
  ],

  rules: ["Règles & Prise en main", "Laborieux", "Difficile", "Gérable", "Départ clair", "Exemplaire"],
  "rules.structure": [
    "Le livret de règles est-il logique, clair et sans erreur ?",
    "Chaotique",
    "Plein de trous",
    "Utilisable",
    "Bien ordonné",
    "Sans faute",
  ],
  "rules.examples": [
    "Les exemples illustrés et les schémas de mise en place aident-ils à comprendre vite ?",
    "Aucun",
    "Peu utiles",
    "Assez utiles",
    "Très utiles",
    "Tout est expliqué",
  ],
  "rules.lookup": [
    "Trouve-t-on vite une règle ou un cas particulier en pleine partie ?",
    "Jamais trouvé",
    "Longue recherche",
    "En feuilletant",
    "Trouvé vite",
    "Immédiat",
  ],
  "rules.teaching": [
    "L’ordre du tour s’explique-t-il facilement à de nouveaux joueurs ?",
    "Presque inexplicable",
    "Laborieux",
    "Correct",
    "Intuitif",
    "Presque évident",
  ],
  "rules.aids": [
    "Y a-t-il des aides de jeu utiles et complètes pour tout le monde ?",
    "Aucune",
    "Incomplètes",
    "Utilisables",
    "Bien faites",
    "Tout y est",
  ],
  "rules.payoff": [
    "L’effort d’apprentissage vaut-il la profondeur obtenue ?",
    "Effort perdu",
    "Trop à apprendre",
    "Équitable",
    "Ça vaut le coup",
    "Vaut chaque minute",
  ],

  mechanics: [
    "Mécaniques & Fluidité",
    "Ça coince partout",
    "Poussif",
    "Ça tourne",
    "Fluide",
    "Sans accroc",
  ],
  "mechanics.core": [
    "Le cœur mécanique est-il innovant, élégant et satisfaisant ?",
    "Éventé",
    "Déjà vu",
    "Solide",
    "Élégant",
    "Neuf et satisfaisant",
  ],
  "mechanics.downtime": [
    "Le temps d’attente entre ses propres tours reste-t-il court ?",
    "Attente interminable",
    "Longues pauses",
    "Supportable",
    "Court",
    "Presque nulle",
  ],
  "mechanics.upkeep": [
    "Les phases d’entretien entre les manches passent-elles sans lourdeur ?",
    "Pure comptabilité",
    "Pesant",
    "Passable",
    "Rapide",
    "Presque invisible",
  ],
  "mechanics.luck": [
    "L’équilibre entre le hasard des dés ou des cartes et la stratégie est-il juste ?",
    "Le hasard décide",
    "Trop aléatoire",
    "Équilibré",
    "Bien dosé",
    "Le hasard n’est qu’une épice",
  ],
  "mechanics.cohesion": [
    "Les phases et les mécanismes s’enchaînent-ils logiquement ?",
    "Rapiécé",
    "Cahoteux",
    "Ça va",
    "Bien imbriqué",
    "D’un seul tenant",
  ],
  "mechanics.runaway": [
    "Le système empêche-t-il un joueur de prendre une avance intouchable ?",
    "Joué d’avance",
    "Impossible à rattraper",
    "Souvent ouvert",
    "Reste tendu",
    "Ouvert jusqu’au bout",
  ],

  depth: ["Profondeur & Rejouabilité", "Plat", "Mince", "Correct", "Profond", "Sans fond"],
  "depth.paths": [
    "Les joueurs ont-ils une vraie liberté sur leur propre chemin vers la victoire ?",
    "Une seule voie",
    "Presque aucun choix",
    "Deux ou trois voies",
    "Beaucoup de voies",
    "Sa propre voie",
  ],
  "depth.meaningful": [
    "Les décisions de chaque tour sont-elles importantes plutôt qu’évidentes ?",
    "Le tour se joue seul",
    "Souvent évident",
    "Parfois délicat",
    "Presque toujours dur",
    "Chaque tour fait mal",
  ],
  "depth.flexibility": [
    "Peut-on changer de tactique quand les plans sont contrariés ?",
    "Le plan ou rien",
    "Peu manœuvrable",
    "Un peu de marge",
    "S’adapte bien",
    "Toujours une issue",
  ],
  "depth.replay": [
    "La mise en place variable ou les paquets de cartes donnent-ils une vraie rejouabilité ?",
    "Une fois suffit",
    "Peu de variété",
    "Quelques parties",
    "Reste frais",
    "Jamais deux fois pareil",
  ],
  "depth.longterm": [
    "La planification à long terme est-elle profonde et récompensée face au gain immédiat ?",
    "On ne fait que réagir",
    "Court terme",
    "Un peu de plan",
    "Récompense les plans",
    "Les grands arcs paient",
  ],
  "depth.playercount": [
    "Le système réagit-il correctement et équitablement aux différents nombres de joueurs ?",
    "Un seul effectif",
    "Bancal",
    "Souvent juste",
    "S’adapte bien",
    "Fort à tout effectif",
  ],

  theme: ["Ambiance & Interaction", "Froid", "Pâle", "Cohérent", "Vivant", "Électrique"],
  "theme.integration": [
    "Le thème se sent-il dans les mécaniques et pas seulement dans les images ?",
    "Pure décoration",
    "Plaqué",
    "Reconnaissable",
    "Porte les mécaniques",
    "Indissociable",
  ],
  "theme.moments": [
    "Le jeu crée-t-il des moments d’émotion — tension, soulagement, surprise — à la table ?",
    "Rien ne se passe",
    "Rarement",
    "De temps en temps",
    "Régulièrement",
    "Frissons constants",
  ],
  "theme.interaction": [
    "L’interaction entre les joueurs, directe ou indirecte, est-elle forte et significative ?",
    "Solitaire",
    "À peine un contact",
    "Indirecte",
    "Nettement sentie",
    "Toujours entremêlée",
  ],
  "theme.talk": [
    "Le jeu suscite-t-il des discussions, des négociations ou une saine rivalité ?",
    "Silence à la table",
    "Peu d’échanges",
    "Quelques échanges",
    "Animé",
    "La table s’enflamme",
  ],
  "theme.quarterbacking": [
    "Les problèmes comme le « quarterbacking » sont-ils évités ?",
    "Une personne décide",
    "Souvent dominé",
    "Ça arrive",
    "Rarement",
    "Jamais un problème",
  ],
  "theme.stories": [
    "Le jeu entre les joueurs produit-il des histoires mémorables ?",
    "Rien ne reste",
    "Vite oublié",
    "Une scène",
    "Plusieurs moments",
    "On en parle encore",
  ],

  pacing: ["Rythme & Tension finale", "Ça traîne", "Inégal", "Correct", "Bien rythmé", "Courbe parfaite"],
  "pacing.length": [
    "La durée réelle correspond-elle à la complexité et au plaisir de l’expérience ?",
    "Bien trop long",
    "Un peu long",
    "Comme il faut",
    "Bien dosé",
    "Pas une minute de trop",
  ],
  "pacing.tension": [
    "La tension monte-t-elle nettement et continûment au fil de la partie ?",
    "Ça retombe",
    "Reste plat",
    "Monte un peu",
    "Monte nettement",
    "Monte jusqu’au bout",
  ],
  "pacing.lulls": [
    "Le jeu évite-t-il les phases pesantes où l’on tourne en rond ?",
    "Toujours pesant",
    "Plusieurs creux",
    "Un creux",
    "Presque aucun",
    "Rapide du début à la fin",
  ],
  "pacing.climax": [
    "Un vrai point culminant dramatique se construit-il vers la fin ?",
    "Ça retombe à plat",
    "Ça s’arrête",
    "Fin correcte",
    "Beau final",
    "Vrai point culminant",
  ],
  "pacing.trigger": [
    "La condition de fin est-elle claire, élégante et équitablement prévisible ?",
    "Sorti de nulle part",
    "Flou",
    "Visible",
    "Clairement annoncé",
    "Élégant et juste",
  ],
  "pacing.again": [
    "La fin donne-t-elle à tous l’envie d’en refaire une tout de suite ?",
    "Plus jamais",
    "Ça suffit pour l’instant",
    "Un jour",
    "Bientôt",
    "Tout de suite",
  ],
};

export const CATALOG: Record<Locale, Record<string, Entry>> = { de: DE, en: EN, fr: FR };

// ---------------------------------------------------------------------------
// Resolving
// ---------------------------------------------------------------------------

/**
 * The catalogue entry for a key, or null when this build has never heard of it.
 *
 * Null is the normal answer, not an error: every question somebody writes
 * themselves has no key at all, and a row synced down from a newer build has one
 * this one cannot resolve. Both cases end at the stored snapshot, which is why
 * the snapshot is written in the first place.
 */
export function seedEntry(locale: Locale, key: string | null | undefined): Entry | null {
  if (!key) return null;
  return CATALOG[locale][key] ?? CATALOG[DEFAULT_LOCALE][key] ?? null;
}

/**
 * The question as this reader should see it, falling back to what was stored.
 *
 * The stored text having to still *be* ours is the load-bearing half. A key says
 * "this row started life as one of ours", not "this row is still ours" — somebody
 * who renames a seeded question keeps the key, and translating over the top of
 * their words would make the rename look like it silently failed. So the
 * catalogue only speaks while the stored text is byte-identical to the English it
 * was seeded with; the first edit hands the row back to its owner for good.
 */
export function localiseName(
  locale: Locale,
  key: string | null | undefined,
  stored: string,
): string {
  const entry = seedEntry(locale, key);
  if (!entry || CATALOG[DEFAULT_LOCALE][key as string]?.[0] !== stored) return stored;
  return entry[0];
}

/**
 * The rubric as this reader should see it, falling back to what was stored.
 *
 * The stored stops keep their `value`s: those are leftover identifiers since the
 * move to stars (see `shared/stars.ts`), but rewriting them here would make a
 * localised rubric compare unequal to the one it replaces for no gain. Length is
 * what the star mapping reads, and it is the same five either way — a catalogue
 * entry whose length disagreed with the snapshot would move every stored rating
 * on that track, so the mismatch falls back rather than translating.
 *
 * Each label is checked on its own against the English it was seeded with, for
 * the reason `localiseName` gives: an owner who rewrote one rung of the ladder
 * keeps that rung, and the four they left alone still read in their language.
 */
export function localiseStops(
  locale: Locale,
  key: string | null | undefined,
  stored: readonly Stop[],
): Stop[] {
  const entry = seedEntry(locale, key);
  const base = CATALOG[DEFAULT_LOCALE][key as string];
  if (!entry || !base || stored.length !== SEED_STOP_COUNT) return stored.slice();
  return stored.map((stop, i) =>
    stop.label === base[i + 1] ? { value: stop.value, label: entry[i + 1] as string } : { ...stop },
  );
}

// ---------------------------------------------------------------------------
// Putting it back
// ---------------------------------------------------------------------------

/*
 * The inverse of the two above, and the reason the editor can show translated
 * text at all.
 *
 * Without it, a German reader who opens the question editor and presses save
 * without typing anything writes the German string into `name` — and since
 * `localiseName` reads a changed `name` as "the owner took this over", that
 * silent no-op would pin the row to German for every reader of that account,
 * permanently. Translating the untouched text back on the way out means the save
 * stores exactly what was already there and nothing happens, which is what
 * pressing save on an unchanged form is supposed to do.
 *
 * Anything the reader actually typed is different from the catalogue entry and
 * is stored as typed.
 */

/** The English to store for text a reader may have been shown translated. */
export function canonicalName(
  locale: Locale,
  key: string | null | undefined,
  edited: string,
): string {
  const entry = seedEntry(locale, key);
  const base = CATALOG[DEFAULT_LOCALE][key as string];
  if (!entry || !base || entry[0] !== edited) return edited;
  return base[0];
}

/** The same for a rubric, rung by rung. */
export function canonicalStops(
  locale: Locale,
  key: string | null | undefined,
  edited: readonly Stop[],
): Stop[] {
  const entry = seedEntry(locale, key);
  const base = CATALOG[DEFAULT_LOCALE][key as string];
  if (!entry || !base || edited.length !== SEED_STOP_COUNT) return edited.slice();
  return edited.map((stop, i) =>
    stop.label === entry[i + 1] ? { value: stop.value, label: base[i + 1] as string } : { ...stop },
  );
}
